import { useSyncExternalStore } from 'react'

/**
 * Instalação do OP como app (PWA). O Chrome dispara `beforeinstallprompt` uma
 * vez, cedo, quando o site pode ser instalado: guardamos o evento para o
 * botão "Instalar" usar quando a pessoa quiser. `iniciarPwa()` roda no
 * main.tsx, antes do React, para não perder o evento.
 */
interface EventoInstalacao extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface EstadoPwa {
  evento: EventoInstalacao | null
  instalado: boolean
}

let estado: EstadoPwa = { evento: null, instalado: false }
const ouvintes = new Set<() => void>()
const emitir = (novo: Partial<EstadoPwa>) => {
  estado = { ...estado, ...novo }
  for (const o of ouvintes) o()
}

/** Aberto como app instalado (sem a barra do navegador). */
export function abertoComoApp(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

/** iPhone/iPad no Safari: não há `beforeinstallprompt`, só "Adicionar à Tela de Início". */
export function ehIos(): boolean {
  if (typeof navigator === 'undefined') return false
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

export function iniciarPwa() {
  if (typeof window === 'undefined') return
  emitir({ instalado: abertoComoApp() })
  window.addEventListener('beforeinstallprompt', (e) => {
    // Sem o mini-banner padrão: o convite é nosso, no lugar certo de cada tela.
    e.preventDefault()
    emitir({ evento: e as EventoInstalacao })
  })
  window.addEventListener('appinstalled', () => emitir({ evento: null, instalado: true }))
}

const assinar = (o: () => void) => {
  ouvintes.add(o)
  return () => {
    ouvintes.delete(o)
  }
}
const ler = () => estado

export interface Instalacao {
  /** O navegador oferece instalação com um toque (Android/Chrome, Edge, desktop). */
  podeInstalar: boolean
  /** Já roda como app, ou acabou de ser instalado. */
  instalado: boolean
  /** iPhone/iPad fora do app: mostrar o passo a passo do Safari. */
  instrucoesIos: boolean
  instalar: () => Promise<boolean>
}

export function useInstalacao(): Instalacao {
  const { evento, instalado } = useSyncExternalStore(assinar, ler, ler)
  return {
    podeInstalar: !!evento && !instalado,
    instalado,
    instrucoesIos: !instalado && !evento && ehIos(),
    instalar: async () => {
      if (!evento) return false
      await evento.prompt()
      const { outcome } = await evento.userChoice
      emitir({ evento: null, ...(outcome === 'accepted' ? { instalado: true } : {}) })
      return outcome === 'accepted'
    },
  }
}

/** Área que este aparelho usa (posto, gestão, painel, diretoria): o app abre direto nela. */
const CHAVE_AREA = 'op:area'
export type Area = '/posto' | '/gestao' | '/painel' | '/diretoria'

export function lembrarArea(area: Area) {
  try {
    localStorage.setItem(CHAVE_AREA, area)
  } catch {
    // Armazenamento bloqueado: o app só abre no início.
  }
}

export function areaLembrada(): Area | null {
  try {
    const v = localStorage.getItem(CHAVE_AREA)
    return v === '/posto' || v === '/gestao' || v === '/painel' || v === '/diretoria' ? v : null
  } catch {
    return null
  }
}

/** "Agora não" no convite: volta a sugerir depois de alguns dias. */
const DIAS_SEM_CONVITE = 7
export function conviteDispensado(contexto: string): boolean {
  try {
    const em = Number(localStorage.getItem(`op:convite-instalar:${contexto}`) ?? 0)
    return Date.now() - em < DIAS_SEM_CONVITE * 86_400_000
  } catch {
    return false
  }
}
export function dispensarConvite(contexto: string) {
  try {
    localStorage.setItem(`op:convite-instalar:${contexto}`, String(Date.now()))
  } catch {
    // sem armazenamento: o convite volta na próxima visita
  }
}
